import { z } from "zod";

import { ExtractAtomicOperationFromDefinition } from "../../atomic-operations/AtomicOperationDefinition.js";
import { DateTimeString, zDateTimeString } from "../../dateTypes.js";
import {
  AgentChatThreadId,
  CellId,
  DataConnectionId,
  HexId,
  MagicEventId,
  UserId,
} from "../../idTypeBrands.js";
import {
  ExploreIntent,
  MagicAcceptType,
  MagicCellPayload,
  MagicCellRunResult,
  MagicClientReferenceInfo,
  MagicEventFailureReason,
  MagicEventSource,
  MagicEventStatus,
  MagicKeyword,
  MagicRequestMode,
  MentionedDataframeName,
  zMentionedDataframeName,
} from "../../MagicTypes.js";
import { RichTextDocument } from "../../richTextTypes.js";
import { SemanticCap } from "../../semanticCaps.js";
import { createHexVersionAtomicOperationDefinition } from "../HexVersionAtomicOperationDefinition.js";

export interface MagicEventPayload {
  id: MagicEventId;
  cellId: CellId | null;
  accepted: boolean | null;
  agentChatThreadId: AgentChatThreadId | null;
  createdDate: DateTimeString;
  dataConnectionId: DataConnectionId | null;
  eventSource: MagicEventSource | null;
  eventType: MagicKeyword;
  result: string | null;
  status: MagicEventStatus | null;
  userId: UserId;
  userProvidedPrompt: string | null;
  parentEventId: MagicEventId | null;
  unsupportedResponse: boolean;
  prevEventId: MagicEventId | null;
  nextEventId: MagicEventId | null;
  revision: number;
  requestMode: MagicRequestMode | null;
  cellRunResult: MagicCellRunResult | null;
  acceptType: MagicAcceptType | null;
  userFacingResult: string | null;
  richTextPrompt: RichTextDocument | null;
  mentionedDataframes: MentionedDataframeName[] | null;
  failureReason: MagicEventFailureReason | null;
  autoTriggered: boolean;
  duration: number | null;
  timeToFirstToken: number | null;
  cellPayload: MagicCellPayload | null;
  relatedProjectIds: HexId[] | null;
  topReferenceInfo: MagicClientReferenceInfo[] | null;
  summary: string | null;
  intent: ExploreIntent | null;
  cancelled: boolean;
}

const CREATE_MAGIC_EVENT_TYPE = "CREATE_MAGIC_EVENT" as const;
// annotated so declaration emit stops inlining the whole payload -- naming
// each field's schema keeps the emitted type a set of references (TS7056)
export const CreateMagicEventPayload: z.ZodObject<{
  id: typeof MagicEventId;
  cellId: z.ZodNullable<typeof CellId>;
  accepted: z.ZodNullable<z.ZodBoolean>;
  agentChatThreadId: z.ZodNullable<typeof AgentChatThreadId>;
  createdDate: typeof zDateTimeString;
  dataConnectionId: z.ZodNullable<typeof DataConnectionId>;
  eventSource: z.ZodNullable<z.ZodNativeEnum<typeof MagicEventSource>>;
  eventType: z.ZodNativeEnum<typeof MagicKeyword>;
  result: z.ZodNullable<z.ZodString>;
  status: z.ZodNullable<z.ZodNativeEnum<typeof MagicEventStatus>>;
  userId: typeof UserId;
  userProvidedPrompt: z.ZodNullable<z.ZodString>;
  parentEventId: z.ZodNullable<typeof MagicEventId>;
  unsupportedResponse: z.ZodBoolean;
  prevEventId: z.ZodNullable<typeof MagicEventId>;
  nextEventId: z.ZodNullable<typeof MagicEventId>;
  revision: z.ZodNumber;
  requestMode: z.ZodNullable<z.ZodNativeEnum<typeof MagicRequestMode>>;
  cellRunResult: z.ZodNullable<z.ZodNativeEnum<typeof MagicCellRunResult>>;
  acceptType: z.ZodNullable<z.ZodNativeEnum<typeof MagicAcceptType>>;
  userFacingResult: z.ZodNullable<z.ZodString>;
  richTextPrompt: z.ZodNullable<typeof RichTextDocument>;
  mentionedDataframes: z.ZodNullable<
    z.ZodArray<typeof zMentionedDataframeName, "many">
  >;
  failureReason: z.ZodNullable<z.ZodNativeEnum<typeof MagicEventFailureReason>>;
  autoTriggered: z.ZodBoolean;
  duration: z.ZodNullable<z.ZodNumber>;
  timeToFirstToken: z.ZodNullable<z.ZodNumber>;
  cellPayload: z.ZodNullable<typeof MagicCellPayload>;
  relatedProjectIds: z.ZodNullable<z.ZodArray<typeof HexId, "many">>;
  topReferenceInfo: z.ZodNullable<
    z.ZodArray<typeof MagicClientReferenceInfo, "many">
  >;
  summary: z.ZodNullable<z.ZodString>;
  intent: z.ZodNullable<z.ZodNativeEnum<typeof ExploreIntent>>;
  cancelled: z.ZodBoolean;
}> = z.object({
  id: MagicEventId,
  cellId: CellId.nullable(),
  accepted: z.boolean().nullable(),
  agentChatThreadId: AgentChatThreadId.nullable(),
  createdDate: zDateTimeString,
  dataConnectionId: DataConnectionId.nullable(),
  eventSource: z.nativeEnum(MagicEventSource).nullable(),
  eventType: z.nativeEnum(MagicKeyword),
  result: z.string().nullable(),
  status: z.nativeEnum(MagicEventStatus).nullable(),
  userId: UserId,
  userProvidedPrompt: z.string().nullable(),
  parentEventId: MagicEventId.nullable(),
  unsupportedResponse: z.boolean(),
  prevEventId: MagicEventId.nullable(),
  nextEventId: MagicEventId.nullable(),
  revision: z.number(),
  requestMode: z.nativeEnum(MagicRequestMode).nullable(),
  cellRunResult: z.nativeEnum(MagicCellRunResult).nullable(),
  acceptType: z.nativeEnum(MagicAcceptType).nullable(),
  userFacingResult: z.string().nullable(),
  richTextPrompt: RichTextDocument.nullable(),
  mentionedDataframes: z.array(zMentionedDataframeName).nullable(),
  failureReason: z.nativeEnum(MagicEventFailureReason).nullable(),
  autoTriggered: z.boolean(),
  duration: z.number().nullable(),
  timeToFirstToken: z.number().nullable(),
  cellPayload: MagicCellPayload.nullable(),
  relatedProjectIds: z.array(HexId).nullable(),
  topReferenceInfo: z.array(MagicClientReferenceInfo).nullable(),
  summary: z.string().nullable(),
  intent: z.nativeEnum(ExploreIntent).nullable(),
  cancelled: z.boolean(),
} satisfies Record<keyof MagicEventPayload, z.ZodTypeAny>);
export type CreateMagicEventPayload = z.infer<typeof CreateMagicEventPayload>;

export const CREATE_MAGIC_EVENT = createHexVersionAtomicOperationDefinition({
  type: CREATE_MAGIC_EVENT_TYPE,
  payloadSchema: CreateMagicEventPayload,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
  },
  writeAuth: {
    kind: "or",
    // If there is not cell already created, defualt to if the user has project
    // edit access
    or: [
      {
        kind: "and",
        and: [
          {
            kind: "hasSemanticCap",
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          },
          {
            kind: "idArgDoesNotExist",
            idArg: "cellId",
            idType: "Cell",
          },
        ],
      },
      // If there is a cell created that we're creating a magic event on the
      // user must have access to the underlying cell to be able to create the
      // magic event linked to that cell. This is important for cases like
      // Components where the user might not have access to the underlying cell.
      {
        kind: "and",
        and: [
          {
            kind: "hasSemanticCap",
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          },
          {
            kind: "hasSemanticCapOnIdArg",
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
            idArg: "cellId",
            idType: "Cell",
          },
        ],
      },
      {
        kind: "and",
        and: [
          {
            kind: "hasSemanticCap",
            cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          },
          {
            kind: "argIs",
            arg: "cellId",
            expectedValue: null,
          },
        ],
      },
    ],
  },
  scopedIds: {
    cellId: {
      scope: "ownedByWrapper",
      idType: "Cell",
      allowNonExistent: true,
    },
    // The event this operation creates. `writeAuth` does not assert the id is
    // unused, so `createdHere` would let a caller reuse an existing event's id
    // from another project. Requiring membership and tolerating the
    // not-yet-created case covers both.
    id: {
      scope: "ownedByWrapper",
      idType: "MagicEvent",
      allowNonExistent: true,
    },
    dataConnectionId: {
      scope: "external",
      reason:
        "Data connections are workspace-scoped and deliberately shared across projects.",
    },
    userId: {
      scope: "external",
      reason: "A user belongs to the workspace, not to any project.",
    },
    agentChatThreadId: {
      scope: "ownedByWrapper",
      idType: "AgentChatThread",
    },
    parentEventId: {
      scope: "ownedByWrapper",
      idType: "MagicEvent",
    },
    prevEventId: {
      scope: "ownedByWrapper",
      idType: "MagicEvent",
    },
    nextEventId: {
      scope: "ownedByWrapper",
      idType: "MagicEvent",
    },
    relatedProjectIds: {
      scope: "external",
      reason: "A hexVersion belongs to a Hex, not the other way round.",
    },
  },
  logSafe: [
    "id",
    "cellId",
    "accepted",
    "agentChatThreadId",
    "createdDate",
    "dataConnectionId",
    "eventSource",
    "eventType",
    "status",
    "userId",
    "parentEventId",
    "unsupportedResponse",
    "prevEventId",
    "nextEventId",
    "revision",
    "requestMode",
    "cellRunResult",
    "failureReason",
    "autoTriggered",
    "duration",
    "intent",
    "timeToFirstToken",
  ],
  conflictId: (op) => `${CREATE_MAGIC_EVENT_TYPE}-${op.payload.id}`,
  excludeFromHistory: true,
  create: (args: MagicEventPayload) => ({
    type: CREATE_MAGIC_EVENT_TYPE,
    payload: {
      ...args,
    },
  }),
});

export type CREATE_MAGIC_EVENT = ExtractAtomicOperationFromDefinition<
  typeof CREATE_MAGIC_EVENT
>;
